import { memo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { labelsAtom } from '../../../models/atoms/company_atoms';
import { company_controller } from '../../../controllers/company_controller';
import { systemTypesSelector } from '../../../models/atoms/device_atoms';
import { useTranslation } from '../../../contexts/TranslationContext'; // Import the translation context
import { alphanumeric_comparator } from '../../../utils/helper_functions';
import { Dialog, DialogActions, DialogTitle, Stack } from '@mui/material';
import { MHMLabel } from '../MHM/MHMLabel';
import { Close } from '@mui/icons-material';
import { MHMButton } from '../MHM/MHMButton';
import { SettingsCard } from './SettingsCard';
import { MHMDialog } from '../MHM/MHMDialog';
import { LabelInput } from '../shared/LabelInput';
import { MHMEditableLabel } from '../MHM/MHMEditableLabel';
import { CheckPermission } from '../CheckPermission';
import { canManageDevicesSelector } from '../../../models/selectors/permission_selectors';

const default_system_types = ['Sewage', 'Stormwater', 'Combined', 'Unknown'];

export const LabelSettingsComponent = memo(function LabelSettingsComponent() {
  const { t } = useTranslation(); // Use the translation hook
  const company = useRecoilValue(labelsAtom);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState('');
  const systemTypes = useRecoilValue(systemTypesSelector);
  const canMangeDevice = useRecoilValue(canManageDevicesSelector);

  const systemTypeLabels = [
    ...default_system_types.slice().sort(alphanumeric_comparator),
    ...systemTypes
      .filter(
        l => company.labels.includes(l) && !default_system_types.includes(l),
      )
      .sort(alphanumeric_comparator),
  ];
  const companyLabels = company.labels
    .filter(
      label =>
        !(systemTypes.includes(label) || default_system_types.includes(label)),
    )
    .sort(alphanumeric_comparator);

  function closeDeleteModal() {
    setLabelToDelete('');
    setIsDeleteModalOpen(false);
  }

  function openDeleteModal(label: string) {
    setLabelToDelete(label);
    setIsDeleteModalOpen(true);
  }

  function deleteLabel() {
    if (!labelToDelete) {
      return;
    }

    company_controller.delete_label(labelToDelete);
    closeDeleteModal();
  }

  return (
    <>
      <SettingsCard id="label" title={t('Labels')} sx={{ overflow: 'unset' }}>
        <Stack gap={2} p={2}>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {systemTypeLabels.map(label => (
              <MHMLabel
                key={label}
                label={t(label)}
                sx={{ flexBasis: 0, maxWidth: 'fit-content' }}
              />
            ))}
            {companyLabels.map(label => (
              <CheckPermission hasPermission={canMangeDevice} key={label}>
                <MHMEditableLabel
                  label={t(label)}
                  onDelete={() => openDeleteModal(label)}
                  deleteIcon={<Close />}
                  sx={{ flexBasis: 0, maxWidth: 'fit-content' }}
                  labelInputProps={{
                    onSave: newLabel =>
                      company_controller.updateLabel(label, newLabel),
                    defaultValue: label,
                  }}
                />
              </CheckPermission>
            ))}
          </Stack>

          <CheckPermission hasPermission={canMangeDevice}>
            <LabelInput
              width={{ xs: '100%', sm: '50%' }}
              flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
              onSave={newLabel => company_controller.create_label(newLabel)}
            />
          </CheckPermission>
        </Stack>
      </SettingsCard>

      <Dialog open={isDeleteModalOpen} onClose={closeDeleteModal}>
        <DialogTitle textAlign="center">{t('Confirm Deletion')}</DialogTitle>

        <MHMDialog.Content>
          {t('This label will be deleted from all devices')}
        </MHMDialog.Content>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <MHMButton onClick={closeDeleteModal} color="inherit">
            {t('Cancel')}
          </MHMButton>
          <MHMButton onClick={deleteLabel} color="error">
            {t('Delete')}
          </MHMButton>
        </DialogActions>
      </Dialog>
    </>
  );
});
